import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button } from 'antd';
import moment from 'moment';
import { canViewOptimiser } from 'library/optimiser';
import {
  observeFinishedRun as observeFinishedRunAction,
  acknowledgeRun as acknowledgeRunAction
} from 'actions/optimiserRunStatus';
import { positionFor } from './layout';

// Roster period dates arrive as date-only strings (e.g. '2026-06-30'); show them
// as 'D MMM YYYY'. Empty/unparseable values render as an em-dash so the label is
// always present (per the run-details layout).
const formatDate = (value) => {
  if (!value) {
    return '—';
  }
  const d = moment(value);
  return d.isValid() ? d.format('D MMM YYYY') : '—';
};

const cardStyle = pos => ({
  position: 'fixed',
  top: `${pos.top}px`,
  right: `${pos.right}px`,
  zIndex: pos.zIndex,
  width: '300px',
  background: '#fff',
  // Explicit text colour: the popup is mounted inside the dark nav header, which
  // sets a light/white text colour that would otherwise be inherited and render
  // invisible on this white card.
  color: 'rgba(0, 0, 0, 0.85)',
  fontSize: '13px',
  border: '1px solid #d9d9d9',
  borderRadius: '4px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  padding: '12px 16px'
});

// Popups for optimiser runs that finished and have not been dismissed in this
// browser. Detection piggy-backs on the badge's 10s poll: whenever the polled
// runStatus shows a finished run this browser has not yet recorded, we observe
// it. The dedup-by-id guard makes componentDidUpdate safe against re-dispatch
// loops (once observed, the id is in the list, so we stop).
export class OptimiserFinishedNotification extends React.Component {
  componentDidMount() {
    this.maybeObserve();
  }

  componentDidUpdate() {
    this.maybeObserve();
  }

  maybeObserve() {
    const {
      enabled,
      runStatus,
      optimiserFinishedRuns,
      observeFinishedRun
    } = this.props;
    if (!enabled) {
      return;
    }
    // One observation per terminal run: the server already scopes
    // recentTerminalRuns to the finished/failed window, so every entry is
    // popup-worthy. Dedup on (runIdentifier, receivedAt) — a re-run of the same
    // run_id finishing again carries a newer receivedAt and must still notify.
    const terminalRuns = (runStatus && runStatus.recentTerminalRuns) || [];
    terminalRuns.forEach((run) => {
      // Cancelled runs are terminal and appear in recentTerminalRuns, but they
      // deliberately never raise a popup (design: no popup for cancellations).
      if (!run.runIdentifier || run.state === 'cancelled') {
        return;
      }
      const receivedAt = run.receivedAt || run.finishedAt || null;
      if (optimiserFinishedRuns.some(
        r => r.runIdentifier === run.runIdentifier && r.receivedAt === receivedAt
      )) {
        return;
      }
      observeFinishedRun({
        runIdentifier: run.runIdentifier,
        receivedAt,
        state: run.state,
        runName: run.runName || '',
        fromDate: run.fromDate || null,
        toDate: run.toDate || null,
        message: run.message || ''
      });
    });
  }

  render() {
    const { enabled, optimiserFinishedRuns, acknowledgeRun } = this.props;
    if (!enabled) {
      return null;
    }
    const pending = optimiserFinishedRuns.filter(r => !r.acknowledged);
    if (pending.length === 0) {
      return null;
    }
    return (
      <div className="bhp-optimiser-finished-popups">
        {pending.map((run, index) => {
          const m = run.receivedAt ? moment(run.receivedAt) : null;
          const finishedAt = m && m.isValid() ? m.format('h:mm a, D MMM YYYY') : null;
          const failed = run.state === 'failed';
          const heading = failed ? 'Optimiser failed' : 'Optimiser finished';
          const accent = failed ? '#cf1322' : '#389e0d';
          return (
            <div key={`${run.runIdentifier}-${run.receivedAt}`} className="bhp-optimiser-finished-popup" style={cardStyle(positionFor(index))}>
              <div
                className="bhp-optimiser-finished-popup__eyebrow"
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  color: '#8c8c8c',
                  marginBottom: 2
                }}
              >
                Optimiser
              </div>
              <div style={{ marginBottom: 6, color: accent, fontWeight: 600 }}>
                {heading}
                {finishedAt && (
                  <span
                    className="bhp-optimiser-finished-popup__time"
                    style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 400 }}
                  > at {finishedAt}
                  </span>
                )}
              </div>
              <div
                className="bhp-optimiser-finished-popup__details"
                style={{ marginBottom: 8, color: 'rgba(0, 0, 0, 0.65)' }}
              >
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Run details:</div>
                <div>{`Name: ${run.runName || '—'}`}</div>
                <div>{`From: ${formatDate(run.fromDate)}`}</div>
                <div>{`To: ${formatDate(run.toDate)}`}</div>
                <div>{`Message: ${run.message || '—'}`}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Button size="small" type="primary" onClick={() => acknowledgeRun({ runIdentifier: run.runIdentifier, receivedAt: run.receivedAt })}>
                  OK
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}

OptimiserFinishedNotification.propTypes = {
  enabled: PropTypes.bool,
  runStatus: PropTypes.shape({
    state: PropTypes.string,
    recentTerminalRuns: PropTypes.arrayOf(PropTypes.shape({
      runIdentifier: PropTypes.string,
      state: PropTypes.string,
      runName: PropTypes.string,
      fromDate: PropTypes.string,
      toDate: PropTypes.string,
      message: PropTypes.string,
      receivedAt: PropTypes.string,
      finishedAt: PropTypes.string
    }))
  }),
  optimiserFinishedRuns: PropTypes.arrayOf(PropTypes.shape({
    runIdentifier: PropTypes.string,
    receivedAt: PropTypes.string,
    acknowledged: PropTypes.bool,
    state: PropTypes.string,
    runName: PropTypes.string,
    fromDate: PropTypes.string,
    toDate: PropTypes.string,
    message: PropTypes.string
  })),
  observeFinishedRun: PropTypes.func,
  acknowledgeRun: PropTypes.func
};

OptimiserFinishedNotification.defaultProps = {
  enabled: false,
  runStatus: { state: 'unknown', recentTerminalRuns: [] },
  optimiserFinishedRuns: [],
  observeFinishedRun() {},
  acknowledgeRun() {}
};

const mapStateToProps = state => ({
  enabled: canViewOptimiser(),
  runStatus: state.optimiserRunStatus ? state.optimiserRunStatus.runStatus : { state: 'unknown' },
  optimiserFinishedRuns: state.optimiserRunStatus && state.optimiserRunStatus.optimiserFinishedRuns
    ? state.optimiserRunStatus.optimiserFinishedRuns
    : []
});

const mapDispatchToProps = dispatch => ({
  observeFinishedRun(payload) {
    return dispatch(observeFinishedRunAction(payload));
  },
  acknowledgeRun(payload) {
    return dispatch(acknowledgeRunAction(payload));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(OptimiserFinishedNotification);
